import { useState, useCallback } from 'react';
import type { UploadState } from '../types';
import { apiClient } from '../api/client';

interface UseUploadResult {
  state: UploadState;
  upload: (params: {
    collectionId: string;
    itemId: string;
    file: File;
  }) => Promise<void>;
  reset: () => void;
}

const initialState: UploadState = {
  isUploading: false,
  progress: 0,
  stage: 'idle',
};

export function useUpload(): UseUploadResult {
  const [state, setState] = useState<UploadState>(initialState);

  const upload = useCallback(
    async ({
      collectionId,
      itemId,
      file,
    }: {
      collectionId: string;
      itemId: string;
      file: File;
    }) => {
      setState({
        isUploading: true,
        progress: 0,
        stage: 'initializing',
      });

      try {
        // 1. Initialize upload
        const initData = await apiClient.initUpload({
          collectionId,
          itemId,
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          size: file.size,
        });

        setState((prev) => ({ ...prev, stage: 'uploading', progress: 0 }));

        // 2. Upload to S3
        await apiClient.uploadToS3(initData.uploadUrl, file, (progress) => {
          setState((prev) => ({ ...prev, progress }));
        });

        setState((prev) => ({ ...prev, stage: 'completing', progress: 100 }));

        // 3. Complete upload
        const completeData = await apiClient.completeUpload({
          collectionId,
          itemId,
          s3Key: initData.s3Key,
          publicUrl: initData.publicUrl,
        });

        setState({
          isUploading: false,
          progress: 100,
          stage: 'done',
          publicUrl: completeData.publicUrl,
        });
      } catch (err) {
        setState({
          isUploading: false,
          progress: 0,
          stage: 'error',
          error: err instanceof Error ? err.message : 'Upload failed',
        });
      }
    },
    []
  );

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  return { state, upload, reset };
}
